/**
 * TranscriptViewer Komponente
 * Zeigt das Transkript einer Voice-Bewertung an
 */

import React, { useState } from 'react';
import type { TranscriptData } from '@/hooks';
import { formatDateTime } from '@/utils';
import { Button } from './Button';
import { Modal } from './Modal';

interface TranscriptViewerProps {
  transcript: TranscriptData;
  onClose: () => void;
}

export function TranscriptViewer({ transcript, onClose }: TranscriptViewerProps): React.ReactElement {
  const [showScores, setShowScores] = useState(false);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimestamp = (timestamp: number): string => {
    const startTime = new Date(transcript.createdAt).getTime();
    const elapsedSeconds = Math.floor((timestamp - startTime) / 1000);
    const mins = Math.floor(elapsedSeconds / 60);
    const secs = elapsedSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const subtitle = `${formatDateTime(transcript.createdAt)} · Dauer: ${formatDuration(transcript.duration)}`;

  const footer = (
    <Button variant="secondary" fullWidth onClick={onClose}>
      Schließen
    </Button>
  );

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Gesprächsverlauf"
      subtitle={subtitle}
      size="2xl"
      flexBody
      maxHeight="85vh"
      footer={footer}
    >
      <div className="px-4 pt-3">
        {/* Toggle Tabs */}
        <div className="flex gap-2 mb-3">
          <button
            onClick={() => setShowScores(false)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-cherry-600 focus:ring-offset-2 ${
              !showScores
                ? 'bg-cherry-600 text-white'
                : 'bg-charcoal-100 text-charcoal-600 hover:bg-charcoal-200'
            }`}
          >
            Transkript
          </button>
          <button
            onClick={() => setShowScores(true)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-cherry-600 focus:ring-offset-2 ${
              showScores
                ? 'bg-cherry-600 text-white'
                : 'bg-charcoal-100 text-charcoal-600 hover:bg-charcoal-200'
            }`}
          >
            Extrahierte Scores ({transcript.extractedScores.length})
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pb-4">
        {!showScores ? (
          /* Transcript View */
          <div className="space-y-3">
            {transcript.entries.length === 0 ? (
              <p className="text-charcoal-500 text-center py-8">
                Keine Transkript-Einträge vorhanden
              </p>
            ) : (
              transcript.entries.map((entry, index) => (
                <div
                  key={index}
                  className={`flex gap-3 ${entry.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  {/* Avatar */}
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                      entry.role === 'user'
                        ? 'bg-cherry-100 text-cherry-600'
                        : 'bg-charcoal-100 text-charcoal-600'
                    }`}
                  >
                    {entry.role === 'user' ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    )}
                  </div>

                  {/* Message */}
                  <div
                    className={`flex-1 max-w-[80%] ${
                      entry.role === 'user' ? 'text-right' : ''
                    }`}
                  >
                    <div
                      className={`inline-block px-4 py-2 rounded-2xl ${
                        entry.role === 'user'
                          ? 'bg-cherry-600 text-white rounded-tr-sm'
                          : 'bg-charcoal-100 text-charcoal-800 rounded-tl-sm'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{entry.text}</p>
                    </div>
                    <p className="text-xs text-charcoal-400 mt-1">
                      {formatTimestamp(entry.timestamp)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          /* Extracted Scores View */
          <div className="space-y-3">
            {transcript.extractedScores.length === 0 ? (
              <p className="text-charcoal-500 text-center py-8">
                Keine Scores extrahiert
              </p>
            ) : (
              transcript.extractedScores.map((score, index) => (
                <div
                  key={index}
                  className="p-3 bg-charcoal-50 rounded-xl"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-charcoal-800 capitalize">
                      {score.dimension.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                    <span
                      className={`font-bold ${
                        score.score >= 4
                          ? 'text-score-good'
                          : score.score >= 3
                          ? 'text-score-ok'
                          : 'text-score-poor'
                      }`}
                    >
                      {score.score} ★
                    </span>
                  </div>
                  {score.note && (
                    <p className="text-sm text-charcoal-600">{score.note}</p>
                  )}
                  <p className="text-xs text-charcoal-400 mt-1">
                    Extrahiert um {formatTimestamp(score.timestamp)}
                  </p>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
